import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';

function slugify(value) {
  if (!value) {
    return '';
  }
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

function StatusDialog({ open, onClose, onSubmit, existingValues }) {
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setLabel('');
      setError('');
    }
  }, [open]);

  const reserved = useMemo(() => new Set(existingValues.map((item) => item.toLowerCase())), [existingValues]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Status name is required.');
      return;
    }
    const slug = slugify(trimmed);
    if (!slug) {
      setError('Please choose a status name that includes letters or numbers.');
      return;
    }
    if (reserved.has(slug)) {
      setError('This status already exists.');
      return;
    }

    onSubmit({ value: slug, label: trimmed });
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={fullScreen}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add a new column</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Status name"
              value={label}
              onChange={(event) => {
                setLabel(event.target.value);
                setError('');
              }}
              error={Boolean(error)}
              helperText={error || 'E.g. Ready for QA, Blocked'}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Add column
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

StatusDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  existingValues: PropTypes.arrayOf(PropTypes.string),
};

StatusDialog.defaultProps = {
  existingValues: [],
};

export default StatusDialog;
